import React, {useState, useEffect,Dispatch, useMemo, useRef, SetStateAction} from "react";
import { InputFilter } from "../inputFilter/inputFilter"; 
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./filter.css"
import "../style/styleText.css"
import { formatFilterLabel, getCuisineDisplayLabel, principalCuisine } from "../types/types";
import { FiCheck } from "react-icons/fi";
import { startTransition } from 'react';

const COLLAPSED_FILTERS = new Set(["Notes","Régime alimentaire","Options pratiques","Parking","Accessibilités"]);
const SHOW_ALL_FILTERS = new Set(["Type de restaurant"]);


const PRICE_MIN = 0;
const PRICE_MAX = 100;
const PRICE_STEP = 10;

const DEFAULT_PRICE: [number, number] = [0, 100];

interface FilterInterface {
    value : string,
    liste: string[], 

    listFilter: (string |[number,number])[],
    setListFilter: Dispatch<React.SetStateAction<(string |[number,number])[]>>,
    searchValue: string,
    setShowAll: Dispatch<React.SetStateAction<number>>,
    setBaseClose?:Dispatch<SetStateAction<boolean>>,
    lockedFilterValues?: Set<string>;
    filterResultCounts?: Map<string, number>;
}


const Filter:React.FC<FilterInterface> =  React.memo(({setBaseClose,value,liste,listFilter,setListFilter,searchValue,setShowAll,lockedFilterValues=new Set<string>(),filterResultCounts}) => {
    const [show,setShow] = useState(!COLLAPSED_FILTERS.has(value))

    const numericFilters = useMemo(() => {
        let price: [number, number] | null = null;

        for (const filter of listFilter) {
            if (price === null &&Array.isArray(filter) &&filter.length === 2 &&filter.every(value => typeof value === "number")) {
                price = filter;
                continue;
            }
            if (price !== null) break;
        }
        return {price};
    }, [listFilter]);
          

    const [minValue, setMinValue] = useState<number>(() => numericFilters.price?.[0] ?? PRICE_MIN);
    const [maxValue, setMaxValue] = useState<number>(() => (numericFilters.price?.[1] ?? PRICE_MAX));
    
    const [showMoreClassicFilters, setShowMoreClassicFilters] = useState(false);
    const classicListToShow = useMemo(() => {
        if (!liste || liste.length === 0) return [];
        if (liste.length < 10) return liste;
        return showMoreClassicFilters ? liste : liste.slice(0, 7);
    }, [liste, showMoreClassicFilters]);

    const applyTimerRef = useRef<number | null>(null);
    const scheduleApply = (fn: () => void, delay = 650) => {
        if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
        applyTimerRef.current = window.setTimeout(() => {
            applyTimerRef.current = null;
            fn();
        }, delay);
    };
    useEffect(() => {
        return () => {
            if (applyTimerRef.current) window.clearTimeout(applyTimerRef.current);
        };
    }, []);

    
    const middle = (minValue + maxValue) / 2;
    const priceWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{x: number;y: number;} | null>(null);
    const priceTouchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);
    const handlePriceTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        const touch = e.touches[0];
        touchStartRef.current = {x: touch.clientX,y: touch.clientY};
        priceTouchDirectionRef.current = null;
    };
    const handlePriceTouchMove = ( e: React.TouchEvent<HTMLDivElement>, which: "min" | "max") => {
        const start = touchStartRef.current;
        if (!start) return;
        const touch = e.touches[0];
        const deltaX = touch.clientX - start.x;
        const deltaY = touch.clientY - start.y;
        if (priceTouchDirectionRef.current === null && Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return;

        if (priceTouchDirectionRef.current === null) {
            priceTouchDirectionRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
        }
        if (priceTouchDirectionRef.current === "vertical") {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        updatePriceFromTouch(e, which);
    };
    const handlePriceTouchEnd = () => {
        touchStartRef.current = null;
        priceTouchDirectionRef.current = null;
    };
    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = parseInt(e.target.value);
      if (value >= maxValue - PRICE_STEP) {
        value = maxValue - PRICE_STEP; 
      }
      setMinValue(value);
      const nextMin = value;
      const nextMax = Math.max(nextMin + PRICE_STEP, maxValue);
      scheduleApply(() => {
        addPrice(nextMin, nextMax);
        triggerCheckAnimation();
      });
    };
    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = parseInt(e.target.value);
      if (value <= minValue + PRICE_STEP) {
        value = minValue + PRICE_STEP; 
      }
      setMaxValue(value);
      const nextMax = value;
        const nextMin = Math.min(nextMax - PRICE_STEP, minValue);
        scheduleApply(() => {
        addPrice(nextMin, nextMax);
        triggerCheckAnimation();
        });
    };
    const updatePriceFromTouch = (e: React.TouchEvent<HTMLDivElement>,which: 'min' | 'max') => {
        e.stopPropagation();

        const wrapper = priceWrapperRef.current;
        if (!wrapper) return;

        const touch = e.touches[0];
        const rect = wrapper.getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, x / rect.width));

        const raw = PRICE_MIN + ratio * (PRICE_MAX - PRICE_MIN);
        const clamped = Math.max(PRICE_MIN, Math.min(PRICE_MAX, raw));
        let stepped = Math.round(clamped / PRICE_STEP) * PRICE_STEP;

        if (which === 'min') {
            if (stepped >= maxValue - PRICE_STEP) {
                stepped = maxValue - PRICE_STEP; 
            }
            const nextMin = Math.min(stepped, maxValue);
            const nextMax = maxValue;
            setMinValue(nextMin);
            scheduleApply(() => {
                addPrice(nextMin, nextMax);
                triggerCheckAnimation();
            }, 450);
        } else {
            if (stepped <= minValue + PRICE_STEP) {
                stepped = minValue + PRICE_STEP; 
            }
            const nextMin = minValue;
            const nextMax = Math.max(stepped, minValue);
            setMaxValue(nextMax);
            scheduleApply(() => {
                addPrice(nextMin, nextMax);
                triggerCheckAnimation();
            }, 450);
        }
    };


    const clickShow = () => {
        if(setBaseClose)setBaseClose(show)
        setShow(!show)
    }

    const clickShowAll = () => {
        if(value === 'Origine de la cuisine') startTransition(() => {setShowAll(1)});
        else if(value === 'Type de restaurant') startTransition(() => {setShowAll(2)});
    }
    

    const addPrice = (nextMin?: number, nextMax?: number) => {
        const min = typeof nextMin === "number" ? nextMin : minValue;
        const max = typeof nextMax === "number" ? nextMax : maxValue;

        const newFilter: [number, number] = [min, max];
        let updatedFilters = [...listFilter];
    
        const index = updatedFilters.findIndex(
            (filter): filter is [number, number] => 
                Array.isArray(filter) && filter.length === 2 && filter.every(num => typeof num === "number")
        );

        if(min === PRICE_MIN && max === PRICE_MAX) {
            if (index !== -1) {
                updatedFilters.splice(index, 1);
                 startTransition(() => {
                    setListFilter(updatedFilters);
                });
            }
            return
        }
    
        if (index !== -1) updatedFilters[index] = newFilter;
        else updatedFilters.push(newFilter);
        startTransition(() => {
            setListFilter(updatedFilters);
        });
    };
    const [showCheck, setShowCheck] = useState(false);
    const checkTimerRef = useRef<number | null>(null);
    const triggerCheckAnimation = () => {
        setShowCheck(true);
        if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
        checkTimerRef.current = window.setTimeout(() => {
            setShowCheck(false);
            checkTimerRef.current = null;
        }, 1500);
    };

    useEffect(() => {
        return () => {
            if (checkTimerRef.current) window.clearTimeout(checkTimerRef.current);
        };
    }, []);


    const orderedCuisineList = useMemo(() => {
        if (value !== "Origine de la cuisine") return liste;
        const filtered = principalCuisine.filter((elem) => liste.includes(elem));
        const remaining = liste.filter((elem) => !principalCuisine.includes(elem));
        return [...filtered, ...remaining];
    }, [liste,value]);
    const visibleCuisineList = useMemo(() => {
        if (orderedCuisineList.length <= 8) return orderedCuisineList;
        if (orderedCuisineList.length <= 10 && showMoreClassicFilters) return orderedCuisineList;
        return orderedCuisineList.slice(0, 7);
    }, [orderedCuisineList, showMoreClassicFilters]);

    const fold = (value: unknown) => {
        if (value == null) return "";
        const s = typeof value === "string" ? value : String(value);
        const lower = s.toLowerCase();
        return /[^\x00-\x7F]/.test(lower)
            ? lower
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/œ/g, "oe")
                .replace(/æ/g, "ae")
                .replace(/ß/g, "ss")
            : lower;
    };

    const listSearch =
        value === "Origine de la cuisine"
            ? (orderedCuisineList ?? [])
            : (liste ?? []);

    const listFolded = React.useMemo(
        () => listSearch.map(raw => ({ raw, norm: fold(raw) })),
        [listSearch]
    );
    const needle = React.useMemo(
        () => fold((searchValue || "").trim()),
        [searchValue]
    );
    const shown = React.useMemo(() => {
        if (!needle) return [];
        return listFolded
            .filter(it => it.norm.includes(needle))
            .map(it => it.raw);
    }, [needle, listFolded]);

    useEffect(() => {
        const nextMin = numericFilters.price?.[0] ?? DEFAULT_PRICE[0];
        const nextMax = numericFilters.price?.[1] ?? DEFAULT_PRICE[1];
        setMinValue(previous =>previous === nextMin ? previous : nextMin);
        setMaxValue(previous =>previous === nextMax ? previous : nextMax);
    }, [numericFilters.price]);


    const isFilterSelect = value.startsWith('Mes filtres')
    const isLockedValue = (elem: string) => {
        const isSelected = listFilter.some((filter) => {
            return typeof filter === "string" && filter === elem;
        });

        return lockedFilterValues.has(elem) && !isSelected;
    };
    const orderedTypeRestoList = useMemo(() => {
        if (value !== "Type de restaurant") return liste;
        return [...liste].sort(
            (a, b) =>
                (filterResultCounts?.get(b) ?? 0) -
                (filterResultCounts?.get(a) ?? 0)
        );
    }, [liste, value, filterResultCounts]);

    return (
        <div className='containerListFilter'>
        { searchValue ? 
            shown && shown.length>0? <div className='listElem'> 
                {
                    shown.map((elem, index) => {
                    const isLocked = isLockedValue(elem);
                    return <div key={`${value}-search-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}><InputFilter value={elem} displayValue={getCuisineDisplayLabel(formatFilterLabel(elem))} show={true} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                    }) 
                }
            </div> : <></>
        :
        <>
            <div className="titleSection pointer" onClick={clickShow}>
                <p className='title midTextBold'>{value}</p>    
                {show?
                            <FiChevronUp className="Chevron" size={20}  />
                        :  <FiChevronDown className="Chevron" size={20} />
                }      
            </div>
            <div className={`listElem ${show ? 'isShow' : ''}`}>            
                {
                    show && <> {
                        value === 'Origine de la cuisine' ? (
                        
                            visibleCuisineList.map((elem, index) => {
                                const isLocked = isLockedValue(elem);
                                return <div key={`${value}-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}>
                                    <InputFilter
                                        value={elem}
                                        show={show}
                                        isCuisine={true}
                                        id={elem + index.toString()}
                                        listFilter={listFilter}
                                        setListFilter={setListFilter}
                                        isLocked={isLocked}
                                    />
                                </div>
                        }))
                        : value === 'Type de restaurant' ? 
                                orderedTypeRestoList && orderedTypeRestoList.length>0 && orderedTypeRestoList.slice(0,7).map((elem, index) => {
                                    const isLocked = isLockedValue(elem);
                                    return <div key={`${value}-${elem}`} className={`elem ${isLocked ? "isLocked" : ""}`}><InputFilter value={elem} displayValue={formatFilterLabel(elem)} show={show} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                                }) 
                                :
                                classicListToShow.map((elem, index) => {
                                    const isLocked = isLockedValue(elem);
                                    return <div key={`${value}-${elem}`} className={`elem ${(isLocked && !value.startsWith('Mes filtres')) ? 'isLocked' : ''}`}><InputFilter value={elem} displayValue={value.startsWith('Mes filtres') ? (isFilterSelect ? getCuisineDisplayLabel(formatFilterLabel(elem)) : formatFilterLabel(elem)) : undefined} show={show} id={elem+index.toString()} listFilter={listFilter} setListFilter={setListFilter} isLocked={isLocked}/></div>
                                }) 
                    }
                    </>
                          
                }
                {isFilterSelect && show && classicListToShow.length >= 1 && <div className="elem deleteFilter">
                    <div className={`content pointer`} onClick={() => setListFilter([])}>
                        <span className={`XsmallText deleteFilter`} >Tout effacer</span>        
                    </div>
                </div>}  
            </div>
            {
                show && <>{
                    value === 'Prix par personne' ? (
                            <div className="slideFilter slidePrice slideDobble XsmallText" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}> 
                                <div className="value flexAllCenter" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}>{minValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${minValue < 30 ? minValue+3 : minValue < 50 ? minValue+((50-minValue)/10) : minValue+((50-minValue)/10)+1}%`}}></div>
                                <div className="value flexAllCenter" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 : maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}>{maxValue === 100 ? '100€+' : maxValue+'€'}</div>
                                <div className="pointerValue" style={{marginLeft:`${maxValue < 40 ?  maxValue + 2 :  maxValue < 50 ?  maxValue + 1 : maxValue <= 90 ? maxValue : maxValue-2}%`}}></div>
                                
                                <div className="patchClose" onTouchStart={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}></div>
                                <div className="range-wrapper" ref={priceWrapperRef}>
                                    <div className="price-touch-zone price-touch-zone-min" style={{ left: 0, width: `${middle}%` }} onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "min")} onTouchEnd={handlePriceTouchEnd}/>
                                    <div className="price-touch-zone price-touch-zone-max" style={{ left: `${middle}%`, width: `${100 - middle}%` }} onTouchStart={handlePriceTouchStart} onTouchMove={(e) => handlePriceTouchMove(e, "max")} onTouchEnd={handlePriceTouchEnd} />
                                    <input type="range" aria-label="Prix minimum en euros" id="minRange" min="0" max="100" step={PRICE_STEP} value={minValue} onChange={(e) => handleMinChange(e)}/>
                                    <input type="range" aria-label="Prix maximum en euros" id="maxRange" min="0" max="100" step={PRICE_STEP} value={maxValue} onChange={(e) => handleMaxChange(e)}/>
                                </div>
                                
                                <div className="slider-track" style={{left: `${Math.min(minValue, 88)}%`,width: `${Math.min(99, Math.max(11, maxValue - minValue))}%`}}></div>
                                {showCheck && <span className="checkAnimation"><FiCheck size={20} color="black" /></span>}
                                <div className="indicationSlide"><span>0 €</span><span>100 € +</span></div>
                            </div> 
                            )
                            :
                            value === 'Origine de la cuisine' ? (
                                orderedCuisineList.length >= 11 ? (
                                    <p className="XsmallText showAllListFilter lowOpacityHover pointer" onClick={clickShowAll}>Tout afficher</p>
                                ) : orderedCuisineList.length >= 9 ? (
                                    <p className="XsmallText showAllListFilter lowOpacityHover pointer" onClick={() => setShowMoreClassicFilters(prev => !prev)}>{showMoreClassicFilters ? "Afficher moins" : "Afficher plus"}</p>
                                ) : undefined
                            ) : SHOW_ALL_FILTERS.has(value) ?  
                                <p className={`XsmallText showAllListFilter lowOpacityHover pointer`} onClick={clickShowAll}>Tout afficher</p> 
                                : show && liste.length >= 10 ? (
                                    <p className="XsmallText showAllListFilter lowOpacityHover pointer" onClick={() => setShowMoreClassicFilters(prev => !prev)}>
                                        {showMoreClassicFilters ? "Afficher moins" : "Afficher plus"}
                                    </p>
                                ) : undefined
                    }</>
            }
            </>}

            
            </div>
    )
});

export {Filter}